import { Component, effect, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Route } from 'src/app/models/enums/Route.enum';
import { Categories, Category, NewIn, Rule } from 'src/app/models/types/Rule/Rule.type';
import { SidenavSection, Tab, Tabs } from 'src/app/models/types/Tabs.type';
import { SharedModule } from 'src/app/modules/shared.module';
import { RulesService } from 'src/app/services/api/rules.service';
import { AuthService } from 'src/app/services/auth.service';
import { SidenavService } from 'src/app/services/sidenav.service';
import { SidenavSectionComponent } from '../sidenav-section/sidenav-section.component';
import packageJson from './../../../../package.json';
import { TermsAndConditionsService } from 'src/app/services/terms-and-conditions.service';

@Component({
  selector: 'app-sidenav',
  imports: [SharedModule, SidenavSectionComponent],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss'
})
export class SidenavComponent {

  private readonly DEELAY: number = 200;

  public readonly version = packageJson.version;

  public opened: WritableSignal<boolean> = signal(false);

  public newIn: WritableSignal<NewIn | undefined> = signal(undefined);

  //Signals che gestiscono la visualizzazione e le animazioni delle singole colonne
  public showFirstColumn: WritableSignal<boolean> = signal(false);
  public showSecondColumn: WritableSignal<boolean> = signal(false);
  public showThirdColumn: WritableSignal<boolean> = signal(false);

  public sidenavColumns: WritableSignal<SidenavSection[]> = signal([]);

  constructor(
    public readonly tecService: TermsAndConditionsService,
    public readonly sidenavService: SidenavService,
    public readonly rulesService: RulesService,
    public readonly authService: AuthService,
    private readonly router: Router,
  ) {

    //Questo effect resetta la rule selezionata
    effect(() => {
      if (!this.sidenavService.opened()) {
        this.handleResetClick();
      } else {
        this.opened.set(true);
      }
    })

    //Questo effect aggiarna tutti i signal con la nuova rule
    effect(() => {
      this.updateAllTabs(this.sidenavService.rule())
    })
  }

  /**
  * Questa funzione viene invocata ogni volta che la rule viene cambiata.
  * Serve a popolare correttamente tutti i menu delle varie categorie
  *
  * @param rule - rule aggiornata
  */
  updateAllTabs(rule?: Rule) {
    if (!!rule) {
      this.sidenavColumns.set(this.parseDiscoverCategoriesToSidenavMenus(rule.discoverCategories));
      this.updateNewInSignal(rule);
    } else {
      this.sidenavColumns.set([]);
    }
  }

  /**
   * Funzione che trasforma l'array di discoverCategories nei menu per il sidenav.
   *
   * @param discoverCategories
   * @returns
   */
  parseDiscoverCategoriesToSidenavMenus(discoverCategories: Categories[]): SidenavSection[] {
    let sidenavColumns: SidenavSection[] = [];

    discoverCategories.forEach(elem => {
      sidenavColumns.push({
        title: elem.name,
        tabs: this.parseToTabs(elem.childs),
        showImages: true
      })
    });
    return sidenavColumns;
  }

  parseToTabs(categories?: Category[]): Tabs {
    let ruleId: number | null | undefined = this.sidenavService.rule()!.id;
    /**
     * Devo controllare se la rule() è figlia della categoria HEW.
     * Se SI: occorre settare ruleId con il valore della rule di HEW
     * Se NO: basta mantenere il valore attuale di ruleId
     */
    let parentRule = this.rulesService.rulesMap.get(ruleId);
    if (parentRule && this.rulesService.isHighEndWatchesRule(this.rulesService.rulesMap.get(parentRule.parentId || -1))) {
      ruleId = parentRule?.parentId;
    }

    if (ruleId) {
      if (categories) {
        return categories.map(cat => {
          let url = `/${Route.Main}/${Route.RulePage}/${ruleId}/${cat.id}`;
          return ({
            id: cat.id,
            name: cat.name,
            routerLink: url,
            assetId: cat.assetId
          } as Tab)
        })
      }
    }

    return [];
  }


  /**
   * Aggiorna il signal NewIn
   *
   * @param rule
   */
  updateNewInSignal(rule: Rule) {
    if (rule.newIn && rule.newIn.materials?.length > 0) {
      this.newIn.set(rule.newIn);
    } else {
      this.newIn.set(undefined);
    }
  }

  handleRuleClick(rule: any) {
    this.sidenavService.selectRule(rule);
    //Con un deelay di 200ms setto i flag a true in modo da poter visualizzare l'animazione CSS
    this.deelayedCallback(() => {
      this.showFirstColumn.set(true);
      this.deelayedCallback(() => {
        this.showSecondColumn.set(true);
        this.deelayedCallback(() => {
          this.showThirdColumn.set(true);
        })
      })
    });
  }

  handleResetClick() {
    this.showThirdColumn.set(false);
    this.deelayedCallback(() => {
      this.showSecondColumn.set(false);
      this.deelayedCallback(() => {
        this.showFirstColumn.set(false);
        this.deelayedCallback(() => {
          this.opened.set(false);
          this.sidenavService.resetRuleRule();
        }, this.DEELAY);
      },
        this.showFirstColumn() ? undefined : 0);
    },
      this.showSecondColumn() ? undefined : 0);
  }

  deelayedCallback(callback: Function, deelay: number = this.DEELAY) {
    setTimeout(callback, deelay)
  }

  isOtherLink(name: string): boolean {
    return name.trim().toLowerCase() === "other";
  }

  handleLinkClick(cat: Category) {
    let url = `${this.rulesService.getRulePageUrl(this.sidenavService.rule()!)}/${cat.id}`;
    this.router.navigate([url]);
  }

  isActive(id: number): boolean {
    let isActive = false;
    let selectedRule = this.sidenavService.rule();
    if (selectedRule) {
      isActive = selectedRule.id === id;
    }
    return isActive;
  }

  isActiveParent(id: number) {
    let isActive = false;
    let selectedRule = this.sidenavService.rule();
    if (selectedRule) {
      let parentId = this.rulesService.rulesMap.get(selectedRule.id)?.parentId;
      isActive = parentId === id;
    }
    return isActive || this.isActive(id);
  }

}
